/**
 * Admin "act as consultant" client-side state.
 *
 * The single source of truth for "is this request impersonating?" is
 * the ``X-Act-As`` HTTP header (sent by ``api.ts`` whenever
 * ``setActAsHeader`` has been called). This context holds the
 * supporting state — who is being impersonated, what to show in the
 * banner — and persists it to sessionStorage so a tab refresh doesn't
 * silently drop impersonation and route the admin back to the admin
 * shell.
 *
 * Why sessionStorage (not localStorage): per-tab isolation. Tab A
 * impersonating Sarah must not affect Tab B's plain admin view.
 *
 * Why synchronous hydration (lazy ``useState`` initializer): on a
 * refresh while impersonating, async hydration would cause a flash of
 * the admin shell before the banner appears. Reading sessionStorage on
 * the first render keeps the banner present from frame 1.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  impersonationService,
  ImpersonationTargetRole,
} from '../services/impersonation';
import { setActAsHeader } from '../services/api';
import { UserRole } from '../types';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'h2okay_impersonation';

export interface ImpersonationState {
  actAsUserId: string;
  actAsUserName: string;
  /** Display label for the banner — consultancy name for consultants,
   * client name for clients/FMs. Always non-empty. */
  actAsContextLabel: string;
  effectiveRole: ImpersonationTargetRole;
  /** The impersonated user's ``client_id``. Populated for client and
   * facility-manager targets; ``null`` for consultants. Read by the
   * portal pages so their scope filters land in the right tenant. */
  targetClientId: string | null;
}

interface ImpersonationContextValue {
  impersonation: ImpersonationState | null;
  /** What role drives the UI (sidebar nav, root-redirect). When
   * impersonating, the impersonated role; otherwise the admin's own. */
  effectiveRole: UserRole | null;
  /** Start a session for a target user-id. The server returns the
   * target's role + display info; this function picks the right
   * effective role and banner label. */
  startActingAs: (userId: string) => Promise<void>;
  stopActingAs: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(
  undefined,
);

function readStoredState(): ImpersonationState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (!parsed.actAsUserId || !parsed.effectiveRole) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredState(state: ImpersonationState | null): void {
  if (typeof window === 'undefined') return;
  if (state === null) {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } else {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
}

export const ImpersonationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Synchronous read — banner is present on the very first render after
  // a refresh, no flash of admin shell.
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(
    () => {
      const initial = readStoredState();
      if (initial) {
        setActAsHeader(initial.actAsUserId);
      }
      return initial;
    },
  );

  const { signOut: realSignOut, currentUser, isH2okayAdmin, loading, bootstrap } = useAuth();
  // Track the latest impersonation so the signOut wrapper closure isn't
  // stale across renders.
  const impersonationRef = useRef(impersonation);
  impersonationRef.current = impersonation;

  // On mount (after auth settles): validate the locally-restored
  // session against the server. If the backend says no active session
  // (or 4xx), clear local state so we don't keep sending X-Act-As for a
  // stale id.
  //
  // Crucial guard: don't decide anything while ``loading`` is true.
  // Otherwise on a refresh-while-impersonating we'd see currentUser=null
  // → isH2okayAdmin=false → drop the state before /auth/me even returns.
  useEffect(() => {
    if (loading) return;
    if (!impersonation) return;
    if (!isH2okayAdmin) {
      // Auth has settled and the signed-in user is not an admin —
      // drop the stale impersonation state.
      writeStoredState(null);
      setActAsHeader(null);
      setImpersonation(null);
      return;
    }
    let cancelled = false;
    Promise.resolve(bootstrap?.impersonation ?? null)
      .then((session) => {
        if (cancelled) return;
        if (!session || session.target_user_id !== impersonation.actAsUserId) {
          // Server says nothing's active (or something different is) —
          // local state is stale.
          writeStoredState(null);
          setActAsHeader(null);
          setImpersonation(null);
        }
      })
      .catch(() => {
        // Network/auth error — leave local state alone. The next manual
        // action will re-validate.
      });
    return () => {
      cancelled = true;
    };
    // Run once per admin session; we don't want to refire on every
    // impersonation change since startActingAs/stopActingAs already
    // keep state in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, isH2okayAdmin, loading, bootstrap]);

  const startActingAs = useCallback(async (userId: string) => {
    const session = await impersonationService.startSession(userId);
    // Banner label: consultancy name for consultants, client name for
    // clients/FMs. Server always returns at least one of these for an
    // impersonatable target.
    const label =
      session.target_role === 'consultant'
        ? session.target_consultancy_name ?? 'Unknown consultancy'
        : session.target_client_name ?? 'Unknown client';
    const next: ImpersonationState = {
      actAsUserId: session.target_user_id,
      actAsUserName: session.target_name,
      actAsContextLabel: label,
      effectiveRole: session.target_role,
      targetClientId: session.target_client_id,
    };
    writeStoredState(next);
    setActAsHeader(next.actAsUserId);
    setImpersonation(next);
  }, []);

  const stopActingAs = useCallback(async () => {
    try {
      await impersonationService.stopActiveSession();
    } catch {
      // Idempotent stop — fall through to clear local state regardless,
      // since the live signal is the header, not the DB row.
    }
    writeStoredState(null);
    setActAsHeader(null);
    setImpersonation(null);
  }, []);

  // Hook AuthContext sign-out: ensure we don't leak an open session row.
  // Wrapping signOut would require AuthContext refactor; the cleaner
  // path is to subscribe to the user vanishing and best-effort close.
  //
  // Same loading guard as above: we only want to trigger this when the
  // *transition* admin → signed-out happens, not the initial
  // "auth still loading" state.
  useEffect(() => {
    if (loading) return;
    if (currentUser === null && impersonationRef.current !== null) {
      // Best-effort fire-and-forget — we can't await in a layout effect.
      void impersonationService.stopActiveSession().catch(() => undefined);
      writeStoredState(null);
      setActAsHeader(null);
      setImpersonation(null);
    }
  }, [currentUser, loading]);

  // Silence unused-binding warnings from the linter for hooks we wired
  // for future use (kept for the explicit signOut handoff if needed).
  void realSignOut;

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      impersonation,
      effectiveRole: impersonation?.effectiveRole ?? null,
      startActingAs,
      stopActingAs,
    }),
    [impersonation, startActingAs, stopActingAs],
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

/**
 * No-op default used when the hook is consumed outside a provider —
 * happens in legacy unit tests that render isolated components without
 * the full App-level provider stack. Returning a safe default avoids
 * forcing every existing test to add ``<ImpersonationProvider>``; real
 * impersonation flows always run inside the provider mounted at App.
 */
const NOOP_VALUE: ImpersonationContextValue = {
  impersonation: null,
  effectiveRole: null,
  startActingAs: async () => {
    /* no provider → ignore */
  },
  stopActingAs: async () => {
    /* no provider → ignore */
  },
};

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  return ctx ?? NOOP_VALUE;
}

export default ImpersonationContext;
